import objectPath from 'object-path';
import { deepCopy } from '../utils/deep';
import GRANT from '../constants/grants';
import { CART_COOKIE_NAME } from '../constants/cart-cookie';

const MAX_WATCH_DOG_INTERVALS = 5;

const ANONYMOUS_TOKEN_STORAGE_PREFIX = 'auth.anonymous';
const USER_TOKEN_STORAGE_PREFIX = 'auth.user';

export default (context, inject) => {
  let crypto;
  if (process.server) {
    try {
      crypto = require('crypto');
    } catch (err) {
      console.log('crypto support is disabled!');
    }
  }

  class AuthService {
    constructor () {
      if (context.$getEnv('API_URL') === null) {
        throw new Error('Check .env file');
      }

      this.isRefreshing = false;
      this.authTokens = [];
    }

    async processAuthorizationCode () {
      const authorizationCode = context.route.query.code || null;
      const redirectPath = context.route.query.redirectPath || null;

      if (authorizationCode !== null) {
        await this.decodeCodeToToken(authorizationCode);
        context.redirect(redirectPath ?? context.route.path);
      }
    }

    hasAnonymousToken () {
      const token = context.$cookies.get(`${ANONYMOUS_TOKEN_STORAGE_PREFIX}.access_token`);

      return typeof token !== 'undefined';
    }

    hasUserToken () {
      const token = context.$cookies.get(`${USER_TOKEN_STORAGE_PREFIX}.access_token`);

      return typeof token !== 'undefined';
    }

    async waitForToken () {
      return await new Promise((resolve, reject) => {
        let watchDogCounter = 0;
        const watchDogInterval = setInterval(() => {
          watchDogCounter += 1;
          if (this.isRefreshing === false) {
            clearInterval(watchDogInterval);
            resolve();
          }

          if (watchDogCounter >= MAX_WATCH_DOG_INTERVALS) {
            clearInterval(watchDogInterval);
            reject(new Error('Refresh failed'));
          }
        }, 1000);
      });
    }

    async getAccessToken () {
      if (this.isRefreshing) {
        await this.waitForToken();
      }

      if (this.hasUserToken()) {
        // Fix for undefined cookie value (set vs get, value is not available in the same request ..)
        if (typeof this.authTokens[USER_TOKEN_STORAGE_PREFIX] !== 'undefined') {
          return this.authTokens[USER_TOKEN_STORAGE_PREFIX];
        }

        return {
          token_type: 'userToken',
          access_token: context.$cookies.get(`${USER_TOKEN_STORAGE_PREFIX}.access_token`),
          refresh_token: context.$cookies.get(`${USER_TOKEN_STORAGE_PREFIX}.refresh_token`),
          expires_at: context.$cookies.get(`${USER_TOKEN_STORAGE_PREFIX}.expires_at`),
        };
      }

      // Fix for undefined cookie value (set vs get, value is not available in the same request ..)
      if (typeof this.authTokens[ANONYMOUS_TOKEN_STORAGE_PREFIX] !== 'undefined') {
        return this.authTokens[ANONYMOUS_TOKEN_STORAGE_PREFIX];
      }

      if (this.hasAnonymousToken()) {
        return {
          token_type: 'anonymousToken',
          access_token: context.$cookies.get(`${ANONYMOUS_TOKEN_STORAGE_PREFIX}.access_token`),
          refresh_token: context.$cookies.get(`${ANONYMOUS_TOKEN_STORAGE_PREFIX}.refresh_token`),
          expires_at: context.$cookies.get(`${ANONYMOUS_TOKEN_STORAGE_PREFIX}.expires_at`),
        };
      }

      return null;
    }

    async refreshToken () {
      const token = await this.getAccessToken();

      if (token && token.token_type === 'userToken') {
        const tokenConfig = {
          tokenType: 'userToken',
          grantType: GRANT.REFRESH_TOKEN,
          data: {
            refresh_token: token.refresh_token,
          },
        };

        return this.doTokenRequest(tokenConfig);
      }

      const tokenConfig = {
        tokenType: 'anonymousToken',
        grantType: GRANT.CLIENT_CREDENTIALS,
      };

      return this.doTokenRequest(tokenConfig);
    }

    isAuthenticated () {
      return this.hasUserToken();
    }

    triggerLoggedInEvent () {
      if (context.$isClient()) {
        document.body.dispatchEvent(new Event('login'));
      }
    }

    triggerLogoutEvent () {
      if (context.$isClient()) {
        document.body.dispatchEvent(new Event('logout'));
      }
    }

    registerLoginHandler (callback) {
      if (context.$isClient()) {
        document.body.addEventListener('login', callback, false);
      }
    }

    registerLogoutHandler (callback) {
      if (context.$isClient()) {
        document.body.addEventListener('logout', callback, false);
      }
    }

    logout () {
      context.$cookies.remove(`${USER_TOKEN_STORAGE_PREFIX}.token_type`);
      context.$cookies.remove(`${USER_TOKEN_STORAGE_PREFIX}.access_token`);
      context.$cookies.remove(`${USER_TOKEN_STORAGE_PREFIX}.refresh_token`);
      context.$cookies.remove(`${USER_TOKEN_STORAGE_PREFIX}.expires_at`);

      this.triggerLogoutEvent();
    }

    loginByPassword (username, password) {
      const tokenConfig = {
        tokenType: 'userToken',
        grantType: GRANT.PASSWORD,
        data: {
          username,
          password,
        },
      };

      return this.doTokenRequest(tokenConfig);
    }

    async loginByAuthorization (redirectPath = null) {
      const authorizeUrl = new URL(`${context.$getEnv('SITE_URL')}/authorize`);
      const authorizeUrlParams = {
        response_type: 'code',
        client_id: context.$getEnv('AUTH_CLIENT_ID'),
        redirect_uri: context.$getEnv('AUTH_CLIENT_REDIRECT_URI'),
        scope: [],
      };

      if (redirectPath !== null) {
        authorizeUrlParams.redirectPath = redirectPath;
      }

      const state = this.generateRandomString();
      await this.setCookie('pkce_state', state);
      const codeVerifier = this.generateRandomString();
      await this.setCookie('pkce_code_verifier', codeVerifier);
      const codeChallenge = await this.pkceChallengeFromVerifier(
        codeVerifier,
        true,
      );
      authorizeUrlParams.code_challenge_method = 'S256';
      authorizeUrlParams.code_challenge = encodeURIComponent(codeChallenge);

      if (process.server) {
        context.redirect(`${authorizeUrl}?${new URLSearchParams(authorizeUrlParams)}`);
      } else {
        window.location.href = `${authorizeUrl}?${new URLSearchParams(authorizeUrlParams)}`;
      }
    }

    getCodeFromUrl (url = null) {
      return new URL(url || window.location.href).searchParams.get('code');
    }

    decodeCodeToToken (codeArg = null) {
      const code = codeArg === null ? this.getCodeFromUrl() : codeArg;

      if (code !== null) {
        const tokenConfig = {
          tokenType: 'userToken',
          grantType: GRANT.AUTHORIZATION_CODE,
          data: {
            code,
            redirect_uri: context.$getEnv('AUTH_CLIENT_REDIRECT_URI'),
          },
        };

        return this.doTokenRequest(tokenConfig);
      }

      return Promise.reject(new Error('Code not defined'));
    }

    loginByClientCredentials () {
      const tokenConfig = {
        tokenType: 'anonymousToken',
        grantType: GRANT.CLIENT_CREDENTIALS,
      };

      return this.doTokenRequest(tokenConfig);
    }

    async doTokenRequest (partialTokenConfig) {
      let tokenConfig = deepCopy(partialTokenConfig);

      this.isRefreshing = true;
      if (tokenConfig.data === undefined) {
        tokenConfig.data = {};
      }

      tokenConfig.data.grant_type = tokenConfig.grantType || '';

      const isPKCERequest = typeof context.$cookies.get('pkce_code_verifier') !== 'undefined'
        && tokenConfig.grantType === GRANT.AUTHORIZATION_CODE;

      if (isPKCERequest) {
        tokenConfig.data.code_verifier = context.$cookies.get('pkce_code_verifier');
        context.$cookies.remove('pkce_state');
        context.$cookies.remove('pkce_code_verifier');
      }

      tokenConfig = this.addClientCredentials(tokenConfig);

      const requestConfig = {
        url: `${context.$getEnv('API_URL')}/token`,
        method: 'POST',
        data: tokenConfig.data,
        headers: {},
      };

      if (context.$cookies.get(CART_COOKIE_NAME)) {
        requestConfig.headers['x-cart-secret'] = context.$cookies.get(CART_COOKIE_NAME);
      }

      if (context.$getEnv('SITE_UUID') !== null) {
        requestConfig.headers['x-site-context'] = context.$getEnv('SITE_UUID');
      }

      return await context.$axios.request(requestConfig).then((response) => {
        if (response.data.access_token !== undefined) {
          const token = response.data;

          const storagePrefix = tokenConfig.tokenType === 'userToken'
            ? USER_TOKEN_STORAGE_PREFIX
            : ANONYMOUS_TOKEN_STORAGE_PREFIX;

          token.expires_at = Math.round(Date.now() / 1000) + token.expires_in;
          const tokenKeys = ['token_type', 'access_token', 'refresh_token', 'expires_at'];
          tokenKeys.forEach((tokenKey) => {
            this.setCookie(`${storagePrefix}.${tokenKey}`, token[tokenKey]);
          });

          this.authTokens[storagePrefix] = token;

          this.isRefreshing = false;
          if (tokenConfig.tokenType === 'userToken' && tokenConfig.grantType !== 'refresh_token') {
            this.triggerLoggedInEvent();
          }

          return response.data;
        } else {
          this.logout();
          console.log('Auth request error, logout!', response.data);
        }
      }).catch((e) => {
        this.logout();
        this.isRefreshing = false;

        console.info('Token request failed, logout!', e);
      });
    }

    addClientCredentials (partialTokenConfig) {
      const tokenConfig = deepCopy(partialTokenConfig);

      let envClientPrefix = 'AUTH_';
      if (tokenConfig.grantType === GRANT.CLIENT_CREDENTIALS) {
        envClientPrefix = 'AUTH_SSR_';
      }

      if (context.$getEnv(`${envClientPrefix}CLIENT_SECRET`) !== null) {
        tokenConfig.data.client_secret = context.$getEnv(`${envClientPrefix}CLIENT_SECRET`);
      }

      tokenConfig.data.client_id = context.$getEnv(`${envClientPrefix}CLIENT_ID`);

      return tokenConfig;
    }

    isAuthenticationRequiredForRoute (route = {}, isLoginDefaultRequired = false) {
      const meta = objectPath.get(route, 'meta.0')
        || objectPath.get(route, 'meta')
        || objectPath.get(context.route, 'meta');

      if (isLoginDefaultRequired === true) {
        return meta.requireAuth !== false;
      }

      return meta.requireAuth === true;
    }

    async pkceChallengeFromVerifier (
      codeVerifier,
      shouldHashValue,
    ) {
      if (shouldHashValue) {
        const hashed = await this._sha256(codeVerifier);

        return this._base64UrlEncode(hashed);
      }

      return codeVerifier; // plain is plain - url-encoded by default
    }

    generateRandomString () {
      if (process.client) {
        const array = new Uint32Array(28); // this is of minimum required length for servers with PKCE-enabled
        window.crypto.getRandomValues(array);

        return Array.from(array, (dec) => (`0${dec.toString(16)}`).substr(-2)).join(
          '',
        );
      } else {
        return this._base64UrlEncodeServer(crypto.randomBytes(32));
      }
    }

    // Dependency: Node.js crypto module
    // https://nodejs.org/api/crypto.html#crypto_crypto
    _sha256 (plain) {
      if (process.client) {
        const encoder = new TextEncoder();
        const data = encoder.encode(plain);

        return window.crypto.subtle.digest('SHA-256', data);
      } else {
        return crypto.createHash('sha256').update(plain).digest();
      }
    }

    _base64UrlEncode (str) {
      if (process.client) {
        // Convert the ArrayBuffer to string using Uint8 array to convert to what btoa accepts.
        // btoa accepts chars only within ascii 0-255 and base64 encodes them.
        // Then convert the base64 encoded to base64url encoded
        //   (replace + with -, replace / with _, trim trailing =)
        return btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
      } else {
        return str.toString('base64')
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=/g, '');
      }
    }

    _base64UrlEncodeServer (str) {
      return str.toString('base64')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
    }

    setCookie (key, value = null, path = '/') {
      const opts = {
        path,
        sameSite: 'lax', // Can only be used with secure: true
        secure: true, // Can only be used with secure: true
        maxAge: (60 * 60 * 24 * 30), // 30 days
      };

      return context.$cookies.set(key, value, opts);
    }
  }

  const authService = new AuthService();

  inject('auth', authService);

  inject('decodeToken', (token) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`;
    }).join(''));

    return JSON.parse(payload);
  });
};
